import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { apiFetch } from "../lib/api-client";
import { currentCustomerMembership, type CustomerMembership, type MembershipLoader } from "./CustomerMembershipTags";
import { MembershipRenewalProposalDialog } from "./MembershipRenewalProposalDialog";

export function BookingMembershipRenewal({ customerId, propertyId, loadMemberships }: {
  customerId: string;
  propertyId: string;
  loadMemberships: MembershipLoader;
}) {
  const [memberships, setMemberships] = useState<CustomerMembership[]>([]);
  const [selected, setSelected] = useState<CustomerMembership | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  useEffect(() => {
    if (!selected) return;
    const closeRenewal = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      event.preventDefault();
      setSelected(null);
    };
    document.addEventListener("keydown", closeRenewal, true);
    return () => document.removeEventListener("keydown", closeRenewal, true);
  }, [selected]);
  useEffect(() => {
    let stale = false;
    void loadMemberships(customerId).then(items => {
      const item = currentCustomerMembership(items, propertyId);
      if (!stale) setMemberships(item && !item.renewedIntoId &&
        ["active", "renewal", "expired", "lapsed"].includes((item.displayStatus || item.status).trim().toLowerCase()) ? [item] : []);
    }).catch(() => { if (!stale) setError("Membership renewal unavailable. Retry by reopening booking."); });
    return () => { stale = true; };
  }, [customerId, propertyId, loadMemberships]);
  const renew = async (membership: CustomerMembership) => {
    if (busy) return;
    setBusy(true); setError(""); setInvoiceId(null);
    try {
      const response = await apiFetch(`/api/v1/workspace/memberships/${encodeURIComponent(membership.id)}/renewals`);
      const body = await response.json();
      if (!response.ok || !Array.isArray(body.data?.renewals)) throw new Error(body.error?.message || "Membership renewal is unavailable.");
      const pending = body.data.renewals.find((item: { status: string; invoiceId: string }) => item.status === "pending");
      if (pending) setInvoiceId(pending.invoiceId);
      else setSelected(membership);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Membership renewal is unavailable."); }
    finally { setBusy(false); }
  };
  return <div className="space-y-2">
    {memberships.map(item => <button key={item.id} type="button" disabled={busy} className="booking-text-action" onClick={() => void renew(item)}>
      Renew membership
    </button>)}
    {invoiceId && <p role="status" className="text-sm"><Link target="_blank" rel="noopener noreferrer" to={`/dashboard?invoice=${encodeURIComponent(invoiceId)}`} className="booking-text-action">Open renewal invoice</Link><span className="block text-xs text-[var(--ink-soft)]">Opens in a new tab. Coverage activates after full payment.</span></p>}
    {error && <p role="alert" className="text-xs text-[var(--accent)]">{error}</p>}
    {selected && createPortal(<MembershipRenewalProposalDialog membershipId={selected.id} currentEndDate={selected.endDate ?? null} onClose={() => setSelected(null)} openInvoice={setInvoiceId} />, document.body)}
  </div>;
}
